/**
 * YNS PDF Data Catalog
 *
 * SECURITY NOTE: This file NO LONGER contains hardcoded PDF URLs.
 * All PDF URLs are now rendered server-side via Locksmith in hub-pdf-data.liquid.
 * This file reads from window.YNS_SECURE_DATA which only contains URLs for
 * content the user has purchased.
 *
 * PDF Access Tags (legacy, now handled by Locksmith):
 * - yns_pdf_fundamentals: Fundamentals study guides
 * - yns_pdf_pharm: Pharmacology study guides
 * - yns_pdf_bundle: All PDFs (bundle purchase)
 */

(function() {
  'use strict';

  // ============================================
  // Initialize from Secure Server Data
  // ============================================
  function initFromSecureData() {
    const secureData = window.YNS_SECURE_DATA;

    if (!secureData) {
      console.warn('YNS_SECURE_DATA not found - PDF data not available');
      return createEmptyDataObject();
    }

    // Build PDF catalog from secure server-rendered data
    const pdfCatalog = {};

    if (secureData.pdfs && Array.isArray(secureData.pdfs)) {
      secureData.pdfs.forEach(pdf => {
        pdfCatalog[pdf.id] = {
          id: pdf.id,
          title: pdf.title,
          description: pdf.description,
          category: pdf.category,
          pages: pdf.pages,
          badge: pdf.badge || null,
          highlights: pdf.highlights || [],
          thumbnail: pdf.thumbnail || null,
          // Security: These fields are only populated if user has access
          hasAccess: pdf.hasAccess || false,
          fileUrl: pdf.fileUrl || null,
          purchaseUrl: pdf.purchaseUrl || null
        };
      });
    }

    return {
      // ============================================
      // PDF Catalog (from secure data)
      // ============================================
      pdfs: pdfCatalog,

      // ============================================
      // Category Configuration
      // ============================================
      categories: secureData.categories || {
        fundamentals: {
          id: 'fundamentals',
          name: 'Fundamentals',
          description: 'Core nursing concepts and skills',
          color: '#46523E',
          purchaseUrl: '/products/nclex-fundamentals-pdf-bundle'
        },
        pharmacology: {
          id: 'pharmacology',
          name: 'Pharmacology',
          description: 'Medication books and drug references',
          color: '#C4785A',
          purchaseUrl: '/products/pharmacology-study-guide-nclex-pharmacology-pages-2026-edition-nursing-ebook'
        }
      },

      // ============================================
      // Access Information (from secure data)
      // ============================================
      access: secureData.access || {
        fundamentals: false,
        pharmacology: false,
        bundle: false,
        videos: false
      },

      // ============================================
      // Helper Methods
      // ============================================
      getPdfById: function(pdfId) {
        return this.pdfs[pdfId] || null;
      },

      getPdfsByCategory: function(category) {
        return Object.values(this.pdfs).filter(pdf => pdf.category === category);
      },

      getAllPdfs: function() {
        return Object.values(this.pdfs);
      },

      getOwnedPdfs: function() {
        return Object.values(this.pdfs).filter(pdf => pdf.hasAccess);
      },

      getLockedPdfs: function() {
        return Object.values(this.pdfs).filter(pdf => !pdf.hasAccess);
      },

      hasAccess: function(pdfId) {
        const pdf = this.getPdfById(pdfId);
        return pdf ? pdf.hasAccess : false;
      },

      getFileUrl: function(pdfId) {
        const pdf = this.getPdfById(pdfId);
        // Only return URL if user has access (server-side verified)
        if (pdf && pdf.hasAccess && pdf.fileUrl) {
          return pdf.fileUrl;
        }
        return null;
      },

      getUnlockUrl: function(pdfId) {
        const pdf = this.getPdfById(pdfId);
        if (!pdf) return '/collections/study-guides';

        // If PDF has a specific purchase URL, use it
        if (pdf.purchaseUrl) {
          return pdf.purchaseUrl;
        }

        // Otherwise, return category bundle URL
        const category = this.categories[pdf.category];
        if (category && category.purchaseUrl) {
          return category.purchaseUrl;
        }

        return '/collections/study-guides';
      },

      // Get featured/recommended PDFs
      getFeaturedPdfs: function(limit = 4) {
        const featured = Object.values(this.pdfs)
          .filter(pdf => pdf.badge)
          .slice(0, limit);

        // Fill with other PDFs if needed
        if (featured.length < limit) {
          const others = Object.values(this.pdfs)
            .filter(pdf => !pdf.badge)
            .slice(0, limit - featured.length);
          return [...featured, ...others];
        }

        return featured;
      },

      // Search PDFs by title or description
      searchPdfs: function(query) {
        const searchTerm = query.toLowerCase();
        return Object.values(this.pdfs).filter(pdf => {
          return pdf.title.toLowerCase().includes(searchTerm) ||
                 pdf.description.toLowerCase().includes(searchTerm) ||
                 pdf.category.toLowerCase().includes(searchTerm);
        });
      }
    };
  }

  // Create empty data object for error cases
  function createEmptyDataObject() {
    return {
      pdfs: {},
      categories: {},
      access: { fundamentals: false, pharmacology: false, bundle: false, videos: false },
      getPdfById: () => null,
      getPdfsByCategory: () => [],
      getAllPdfs: () => [],
      getOwnedPdfs: () => [],
      getLockedPdfs: () => [],
      hasAccess: () => false,
      getFileUrl: () => null,
      getUnlockUrl: () => '/collections/study-guides',
      getFeaturedPdfs: () => [],
      searchPdfs: () => []
    };
  }

  // ============================================
  // Initialize
  // ============================================
  window.YNS_PDF_DATA = initFromSecureData();

})();

// Make it available as a module if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.YNS_PDF_DATA;
}
